/**
 * 384: a copy of a built (and baked) model — its tree cloned, sharing geometry and materials, and every part its record names
 * (an Object3D in the tree, a vector, an array or a plain record of them) remapped onto the copy. The person's baked figure
 * (personModel.ts bakedPersonModel, lever 6) and the dropship (dropshipLooks.ts bakedDropship) are copied this way: one
 * build and bake a look, a copy a view. A part outside the tree, or a value that is neither plain nor one of those, throws —
 * a copy never shares something the original moves. Presentation only.
 */
import * as THREE from 'three';

export function copyModel<T extends { root: THREE.Object3D }>(t: T): T {
  const root = t.root.clone(true), to = new Map<THREE.Object3D, THREE.Object3D>();
  const pair = (a: THREE.Object3D, b: THREE.Object3D): void => { to.set(a, b); a.children.forEach((c, i) => pair(c, b.children[i])); };
  pair(t.root, root);
  const done = new Map<object, unknown>();   // (a record named twice — the gunship's ship is its aircraft's — is one record in the copy too)
  const remap = (v: unknown): unknown => {
    if (v instanceof THREE.Object3D) { const c = to.get(v); if (!c) throw new Error('copyModel: a part outside the tree'); return c; }
    if (v === null || typeof v !== 'object') return v;
    const had = done.get(v);
    if (had !== undefined) return had;
    if (v instanceof THREE.Vector3 || v instanceof THREE.Quaternion || v instanceof THREE.Euler) { const c = v.clone(); done.set(v, c); return c; }
    if (Array.isArray(v)) { const a: unknown[] = []; done.set(v, a); for (const x of v) a.push(remap(x)); return a; }
    if (Object.getPrototypeOf(v) !== Object.prototype) throw new Error('copyModel: a part it cannot copy');
    const o: Record<string, unknown> = {};
    done.set(v, o);
    for (const [k, x] of Object.entries(v)) o[k] = remap(x);
    return o;
  };
  return remap(t) as T;
}

/** Mark every geometry and material under `root` shared, so disposing a copy (meshBake.ts disposeObjectTree) leaves them for the next. */
export function markTreeShared(root: THREE.Object3D): void {
  root.traverse(o => {
    const { geometry, material } = o as THREE.Mesh;
    if (geometry) geometry.userData.shared = true;
    for (const m of Array.isArray(material) ? material : material ? [material] : []) m.userData.shared = true;
  });
}
